import React, { useEffect } from 'react';
import { Button } from 'antd';
import { ButtonGroup, Drawer, Form } from '@common/components';
import { useMutation } from '@common/hooks';
import { createMessage } from 'works/legacy/lib/message-helper';
import { EDIT_UNIT_SHIFT_GAP_TAG_MUTATION } from '@scheduling/graphql/mutations/unit-shift-gap-tag';
import {
  UnitShiftGapTagForm,
  unitShiftGapTagFormNameErrorsHandler,
} from '@scheduling/components';

export function UnitShiftGapTagUpdateDrawer({
  open,
  onClose,
  tagId,
  tagName,
  tagDescription,
}: {
  open: boolean;
  onClose: () => void;
  tagId?: string;
  tagName?: string;
  tagDescription?: string;
}) {
  const [form] = Form.useForm();

  const [editTag, { loading }] = useMutation(EDIT_UNIT_SHIFT_GAP_TAG_MUTATION, {
    refetchQueries: ['UnitShiftGapTagsQuery'],
  });

  useEffect(() => {
    if (open) {
      setTimeout(() => {
        form.setFieldsValue({
          name: tagName ?? '',
          description: tagDescription ?? '',
        });
      });
    }
  }, [open, tagName, tagDescription, form]);

  function handleClose() {
    form.resetFields();
    onClose();
  }

  async function handleFinish(values: { name: string; description?: string }) {
    if (!tagId) return;

    try {
      const response = await editTag({
        variables: {
          id: tagId,
          input: {
            name: values.name,
            description: values.description || null,
          },
        },
      });

      const mutationErrors = response?.data?.editShiftGapTag?.errors;

      if (mutationErrors?.length) {
        unitShiftGapTagFormNameErrorsHandler(form, mutationErrors);
        return;
      }

      if (response?.errors?.length) {
        createMessage(
          'error',
          response.errors.map((e) => e.message).join(', ')
        );
        return;
      }

      createMessage('success', 'Shift tag updated successfully.');
      handleClose();
    } catch {
      createMessage(
        'error',
        "Shift tag couldn't be saved at this time. Try again later or contact support."
      );
    }
  }

  const footer = (
    <ButtonGroup>
      <Button size="large" onClick={handleClose}>
        Cancel
      </Button>
      <Button
        type="primary"
        size="large"
        loading={loading}
        onClick={form.submit}
      >
        Save
      </Button>
    </ButtonGroup>
  );

  return (
    <Drawer
      open={open}
      title="Edit Shift Tag"
      footer={footer}
      onClose={handleClose}
    >
      <UnitShiftGapTagForm form={form} onFinish={handleFinish} />
    </Drawer>
  );
}
